import { useRef, useEffect, useState } from "react";
import { useScrollDirection } from "./useScrollDirection";

interface UseScrollVisibilityOptions {
  threshold?: number;
  throttleMs?: number;
  enabled?: boolean;
  /**
   * 이게 참을 돌려주는 동안 **숨김 상태를 바꾸지 않는다** (#5672).
   *
   * 프로그램적 스크롤을 사용자 의도로 읽지 않기 위한 자리다. 소비처가 「내가 지금
   * 스크롤 중」을 아는 유일한 쪽이므로 **판정을 주입받는다.**
   *
   * ⚠️ **함수로 받는다.** 값으로 받으면 그 렌더 시점의 스냅샷이라 스크롤 이벤트마다
   * 다시 물을 수 없다.
   *
   * ⚠️ **기본값은 없다** — 넘기지 않은 소비처(`GameDetailPageTemplate`)는 동작이
   * 그대로다. 그쪽의 프로그램적 스크롤(목차 클릭)은 **사용자가 방금 누른 것**이라
   * 헤더가 나오는 편이 맞다.
   */
  suspended?: () => boolean;
}

/**
 * 스크롤 방향에 따라 요소를 자동으로 보이거나 숨깁니다.
 * - 아래로 스크롤: 요소를 위로 이동시켜 숨김
 * - 위로 스크롤: 요소를 다시 보이게 함
 *
 * ⚠️ 숨김은 transform 이라 **레이아웃 박스는 그대로 남는다** — targetRef 의 부모가
 * 스티키 컨테이너면 내용물이 사라진 뒤에도 그 박스가 계속 포인터를 먹어서 아래 요소가
 * 클릭 불가가 된다 (#5162 · docs/pitfalls.md #36 「스크롤로 숨는 스티키 헤더」).
 *
 * 그래서 `containerProps` 를 **부모에 그대로 펼쳐야 한다** — 이 규칙을 주석이 아니라
 * 반환값으로 들고 있어서, 새 소비처가 잊으면 "안 쓴 반환값" 으로 드러난다.
 *
 * ```tsx
 * const { targetRef, hidden, containerProps } = useHiddenOnScroll();
 * <div {...containerProps} className="sticky top-0 z-30">
 *   <div ref={targetRef}>...</div>
 * </div>
 * ```
 */
export const useHiddenOnScroll = <T extends HTMLElement = HTMLDivElement>({
  threshold = 20,
  throttleMs = 100,
  enabled = true,
  suspended,
}: UseScrollVisibilityOptions = {}) => {
  const targetRef = useRef<T>(null);
  const targetHeight = useRef<number>(0);
  const { direction, scrollY } = useScrollDirection({ threshold, throttleMs });
  const [hidden, setHidden] = useState<boolean>(false);

  useEffect(() => {
    if (!targetRef.current) return;
    const updateHeight = () => {
      targetHeight.current =
        targetRef.current?.getBoundingClientRect().height || 0;
    };
    updateHeight();
    const resizeObserver = new window.ResizeObserver(updateHeight);
    resizeObserver.observe(targetRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!targetRef.current || !enabled) return;

    // 상단 근처에서는 항상 표시
    if (scrollY <= threshold) {
      if (hidden) {
        show(targetRef.current);
        setHidden(false);
      }
      return;
    }

    if (direction === "none") return;

    /**
     * ★**프로그램적 스크롤 동안에는 상태를 바꾸지 않는다** (#5672).
     *
     * 이 자리가 `scrollY <= threshold` **아래**인 것이 중요하다 — 맨 위로 돌아가면
     * 헤더는 프로그램적 스크롤이었든 아니든 보여야 한다(그게 이 컴포넌트의 다른
     * 불변식이다).
     *
     * 창이 끝나면 더 이상 스크롤 이벤트가 없어 이 effect 가 다시 안 돌므로, **그때의
     * 숨김 상태가 그대로 남는다** — 미뤄 둔 목표를 들고 있지 않는다.
     */
    if (suspended?.()) return;

    if (direction === "down") {
      targetRef.current.style.transform = `translateY(-${targetHeight.current}px)`;
      targetRef.current.style.opacity = "0";
      setHidden(true);
    } else if (direction === "up") {
      show(targetRef.current);
      setHidden(false);
    }
  }, [direction, enabled, scrollY, threshold, hidden, suspended]);

  return {
    targetRef,
    hidden,
    /** 스티키 부모에 펼칠 props — 숨은 동안 남는 박스가 포인터를 먹지 않게 한다 */
    containerProps: {
      style: { pointerEvents: hidden ? ("none" as const) : undefined },
    },
  };
};

const show = (element: HTMLElement) => {
  element.style.transform = "translateY(0px)";
  element.style.opacity = "1";
};
